/**
 * Unseen Achievements Page
 * Displays unseen achievements with animations and report readiness polling
 */

import React, { useMemo, useEffect, useState, useRef } from 'react'
import { Box, Typography, LinearProgress } from '@mui/material'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { useNavigate } from 'react-router-dom'
import { loadDevModeFromStorage } from '../../../shared/libs/devModePersistence'
import useAppState from '../../../hooks/useAppState'
import {
  useGetUserAchievements,
  useMarkAchievementsSeen,
  useFeedbackReadiness,
  useUnseeAllAchievements
} from '../../../apiLayer/queryHooks/queries'
import { GlassyActionButton, Spinner } from '../../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../../constants/colors'
import { GamerRoutes } from '../../../config/routes/routeEnums'
import { transformSessionAchievementToDisplay } from '../achievements/helpers/sessionAchievementToAchievement'
import { transformUserAchievementsToAchievements } from '../achievements/helpers/userAchievementsTransformer'
import { PageWrapper } from '../../../shared/components'
import { UnseenAchievementsView } from './components/UnseenAchievementsView'
import { SeenAchievementsView } from './components/SeenAchievementsView'
import { BookDemoModal } from '../../../features/landing/components/BookDemoModal'

// Feedback readiness polling is intentionally delayed by 1 minute after entering this page.
// We persist the first-visit timestamp so refreshes don't restart the delay.
const FEEDBACK_READINESS_DELAY_MS = 60_000
const FEEDBACK_READINESS_TIMESTAMP_MAX_AGE_MS = 6 * 60 * 60 * 1000 // 6 hours
// Progress bar animation - fills from 0% to 100% over 5 minutes
// Progress fills over 5 minutes (300 seconds)
const PROGRESS_DURATION = 300000 // 5 minutes in milliseconds
const UPDATE_INTERVAL = 100 // Update every 100ms for smooth animation
const PROGRESS_INCREMENT = (100 / PROGRESS_DURATION) * UPDATE_INTERVAL

/**
 * UnseenAchievementsPage - Displays unseen achievements with animations
 */
export const UnseenAchievementsPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state } = useAppState()
  const { data: achievementsResponse, isLoading: isLoadingAchievements } = useGetUserAchievements()
  const markSeenMutation = useMarkAchievementsSeen()
  const unseeAllMutation = useUnseeAllAchievements()
  const [isDevModeEnabled, setIsDevModeEnabled] = useState(() => loadDevModeFromStorage())
  const [isBookDemoModalOpen, setIsBookDemoModalOpen] = useState(false)

  const feedbackReadinessStartAtStorageKey = `feedbackReadinessStartAt:gamer:${state.user?.id ?? 'anonymous'}`

  const getOrInitFeedbackReadinessStartAt = () => {
    const now = Date.now()
    const raw = window.localStorage.getItem(feedbackReadinessStartAtStorageKey)
    const parsed = raw ? Number(raw) : NaN
    const isValid = Number.isFinite(parsed) && parsed > 0

    if (!isValid || now - parsed > FEEDBACK_READINESS_TIMESTAMP_MAX_AGE_MS) {
      window.localStorage.setItem(feedbackReadinessStartAtStorageKey, String(now))
      return now
    }

    return parsed
  }

  const [feedbackReadinessStartAt] = useState(() => getOrInitFeedbackReadinessStartAt())
  const [isFeedbackReadinessEnabled, setIsFeedbackReadinessEnabled] = useState(() => {
    return Date.now() - feedbackReadinessStartAt >= FEEDBACK_READINESS_DELAY_MS
  })

  // Enable feedback readiness query after remaining delay (if any)
  useEffect(() => {
    if (isFeedbackReadinessEnabled) return

    const elapsed = Date.now() - feedbackReadinessStartAt
    const remaining = Math.max(0, FEEDBACK_READINESS_DELAY_MS - elapsed)
    const timeoutId = window.setTimeout(() => setIsFeedbackReadinessEnabled(true), remaining)
    return () => window.clearTimeout(timeoutId)
  }, [feedbackReadinessStartAt, isFeedbackReadinessEnabled])

  const { data: feedbackReadinessResponse } = useFeedbackReadiness({
    enabled: isFeedbackReadinessEnabled
  })

  const getInitialProgress = () => {
    const elapsed = Date.now() - feedbackReadinessStartAt
    const percent = (elapsed / PROGRESS_DURATION) * 100
    return Math.max(0, Math.min(100, percent))
  }

  const [progress, setProgress] = useState(() => getInitialProgress())
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Check dev mode on mount and listen for changes
  useEffect(() => {
    const checkDevMode = () => {
      const enabled = loadDevModeFromStorage()
      setIsDevModeEnabled(enabled)
    }

    // Initial check
    checkDevMode()

    // Listen for storage events (if dev mode is changed in another tab/window)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'interax_dev_mode_enabled') {
        checkDevMode()
      }
    }

    window.addEventListener('storage', handleStorageChange)

    // Check when window regains focus (in case dev mode was enabled in another page)
    const handleFocus = () => {
      checkDevMode()
    }

    window.addEventListener('focus', handleFocus)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [])

  // Extract unseen achievements from UserAchievements
  const unseenAchievements = useMemo(() => {
    if (!achievementsResponse || !achievementsResponse.success || !achievementsResponse.data) {
      return []
    }

    const userAchievements = achievementsResponse.data

    if (!userAchievements.unseenAchievements || userAchievements.unseenAchievements.length === 0) {
      return []
    }

    // Transform unseen achievements to display format
    return userAchievements.unseenAchievements
      .map((unlock) => transformSessionAchievementToDisplay(unlock))
      .filter((achievement): achievement is NonNullable<typeof achievement> => achievement !== null)
  }, [achievementsResponse])
  // Transform all achievements for display (not just unseen)
  const allAchievements = useMemo(() => {
    if (!achievementsResponse || !achievementsResponse.success || !achievementsResponse.data) {
      return []
    }

    const userAchievements = achievementsResponse.data
    return transformUserAchievementsToAchievements(userAchievements)
  }, [achievementsResponse])

  // Check if user has any achievements at all (not just unseen)
  const hasAnyAchievements = useMemo(() => {
    if (!achievementsResponse || !achievementsResponse.success || !achievementsResponse.data) {
      return false
    }

    const userAchievements = achievementsResponse.data
    // Check if achievements object has any non-null values
    const achievements = userAchievements.achievements || {}
    const hasAchievements = Object.values(achievements).some((progress) => progress !== null)
    return hasAchievements
  }, [achievementsResponse])

  // Check if user has no achievements at all (for showing no achievements message)
  const hasNoAchievements = useMemo(() => {
    return (
      !isLoadingAchievements &&
      !hasAnyAchievements &&
      achievementsResponse?.success &&
      unseenAchievements.length === 0 &&
      allAchievements.length === 0
    )
  }, [
    isLoadingAchievements,
    hasAnyAchievements,
    achievementsResponse?.success,
    unseenAchievements.length,
    allAchievements.length
  ])

  // Track if we're showing unseen achievements
  const isShowingUnseen = unseenAchievements.length > 0

  // Check if feedback is ready
  const isFeedbackReady = feedbackReadinessResponse?.data?.isReady ?? false
  const hasFeedbackError = feedbackReadinessResponse?.data?.error ?? false

  useEffect(() => {
    // Clear any existing interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
      progressIntervalRef.current = null
    }

    // If feedback is ready or has error, set progress to 100% and stop
    if (isFeedbackReady || hasFeedbackError) {
      setProgress(100)
      return
    }

    // Resume progress from persisted start timestamp
    setProgress(getInitialProgress())

    // Animate progress over 5 minutes
    progressIntervalRef.current = setInterval(() => {
      setProgress((prev) => {
        const next = prev + PROGRESS_INCREMENT
        return next >= 100 ? 100 : next
      })
    }, UPDATE_INTERVAL)

    // Cleanup function
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
        progressIntervalRef.current = null
      }
    }
  }, [isFeedbackReady, hasFeedbackError, PROGRESS_INCREMENT])

  // Note: We no longer redirect - we show a message instead

  // Handle marking achievement as seen (called individually from UnseenAchievementsView)
  const handleMarkSeen = (achievementId: string) => {
    markSeenMutation.mutate([achievementId])
  }

  if (isLoadingAchievements) {
    return (
      <PageWrapper
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: INTERAX_COLORS.offBlack
        }}
      >
        <Spinner size="large" color="sun" />
      </PageWrapper>
    )
  }

  // If user has no achievements at all, show message with contact button
  if (hasNoAchievements) {
    return (
      <PageWrapper
        sx={{
          minHeight: '100vh',
          height: '100vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          backgroundImage: 'url(/assets/img/compres_achivements_background.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          position: 'relative'
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            maxWidth: '600px'
          }}
        >
          <Typography
            sx={{
              fontFamily: 'DM Sans, sans-serif',
              fontStyle: 'normal',
              fontWeight: 700,
              fontSize: '57.485px',
              lineHeight: '75px',
              textAlign: 'center',
              background: `linear-gradient(128.64deg, #7A96AC 14.45%, #EAEFF3 27.29%, #C2D4E1 36.93%, #FFFFFF 49.55%, #D4DEE5 58.33%, #ABBDC8 70.46%, #BCCAD7 82.6%), radial-gradient(70.31% 37.22% at 30.94% 23.13%, #FF9FEA 0%, rgba(255, 255, 255, 0) 100%), radial-gradient(156.71% 156.71% at -16.25% 73.33%, #A349EF 42.19%, #FF65E6 100%), #FFFFFF`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              textShadow:
                '0px 2.69133px 53.4611px rgba(255, 255, 255, 0.25), 0px 2.69133px 44.9022px rgba(203, 218, 230, 0.5)'
            }}
          >
            {t('gamer.achievements.unseen.noAchievements.title', 'Sadly you have no achievements')}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Lexend Deca',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '18px',
              lineHeight: '26px',
              color: INTERAX_COLORS.typographyFont,
              opacity: 0.8,
              textAlign: 'center'
            }}
          >
            {t(
              'gamer.achievements.unseen.noAchievements.subtitle',
              'You need to play a game to get achievements. Or maybe our analyses failed. Please contact us.'
            )}
          </Typography>
          <GlassyActionButton variant="orange" onClick={() => setIsBookDemoModalOpen(true)}>
            {t('gamer.achievements.unseen.noAchievements.contactButton', 'Contact Us')}
          </GlassyActionButton>
        </Box>
        <BookDemoModal isOpen={isBookDemoModalOpen} onClose={() => setIsBookDemoModalOpen(false)} />
      </PageWrapper>
    )
  }

  return (
    <PageWrapper
      sx={{
        minHeight: '100vh',
        height: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 4,
        // Background image
        backgroundImage: 'url(/assets/img/compres_achivements_background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        overflow: 'visible' // Allow achievements to animate outside page bounds
      }}
    >
      {/* DEV Button - Unsee Achievements (only visible in dev mode) */}
      {/* Debug: Always render button container to verify visibility logic */}
      {isDevModeEnabled ? (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1000,
            display: 'flex',
            gap: 1,
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              transform: 'scale(0.7)',
              transformOrigin: 'top right'
            }}
          >
            <GlassyActionButton
              variant="orange"
              onClick={() => {
                unseeAllMutation.mutate(
                  {},
                  {
                    onSuccess: () => {
                      // Only reload on success
                      window.location.reload()
                    },
                    onError: (error) => {
                      // Handle error without refreshing
                      console.error('❌ Failed to unsee achievements:', error)
                      // Optionally show error to user (could use a toast/snackbar here)
                    }
                  }
                )
              }}
              disabled={unseeAllMutation.isPending}
            >
              {unseeAllMutation.isPending
                ? 'Loading...'
                : t('gamer.achievements.unseen.unseeButton', 'Unsee Achievements')}
            </GlassyActionButton>
          </Box>
        </Box>
      ) : null}

      {/* Top Section - Title (always shown) and Ready Button/Spinner based on feedback status */}
      <Box
        sx={{
          mt: 8,
          textAlign: 'center',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3
        }}
      >
        <Typography
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontStyle: 'normal',
            fontWeight: 700,
            fontSize: '57.485px',
            lineHeight: '75px',
            textAlign: 'center',
            background: `linear-gradient(128.64deg, #7A96AC 14.45%, #EAEFF3 27.29%, #C2D4E1 36.93%, #FFFFFF 49.55%, #D4DEE5 58.33%, #ABBDC8 70.46%, #BCCAD7 82.6%), radial-gradient(70.31% 37.22% at 30.94% 23.13%, #FF9FEA 0%, rgba(255, 255, 255, 0) 100%), radial-gradient(156.71% 156.71% at -16.25% 73.33%, #A349EF 42.19%, #FF65E6 100%), #FFFFFF`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textFillColor: 'transparent',
            textShadow:
              '0px 2.69133px 53.4611px rgba(255, 255, 255, 0.25), 0px 2.69133px 44.9022px rgba(203, 218, 230, 0.5)',
            flex: 'none',
            alignSelf: 'stretch',
            flexGrow: 0,
            whiteSpace: 'pre-line'
          }}
        >
          {hasFeedbackError
            ? t(
                'gamer.achievements.unseen.feedbackError.title',
                "Oh no. Something went wrong or we don't have correct data for generating your feedback. Please contact support."
              )
            : !isFeedbackReady && !hasFeedbackError
              ? t('gamer.achievements.unseen.feedbackGenerating', 'Your latest session feedback \n is being generated')
              : t('gamer.achievements.unseen.feedbackReady.title', "Now it's done! Let's explore your feedback")}
        </Typography>

        {/* Show subtitle when feedback is not ready and no error */}
        {!isFeedbackReady && !hasFeedbackError && (
          <Typography
            sx={{
              fontFamily: 'Lexend Deca',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '18px',
              lineHeight: '26px',
              color: INTERAX_COLORS.typographyFont,
              opacity: 0.8,
              textAlign: 'center'
            }}
          >
            {t(
              'gamer.achievements.unseen.feedbackGeneratingSubtitle',
              'This can take a few minutes. Good time to grab a coffee'
            )}
          </Typography>
        )}

        {/* Show progress bar when feedback is not ready and no error */}
        {!isFeedbackReady && !hasFeedbackError && (
          <Box
            sx={{
              width: '100%',
              maxWidth: '600px',
              paddingX: 3
            }}
          >
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: INTERAX_COLORS.sun,
                  borderRadius: 4
                }
              }}
            />
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: 600,
                color: INTERAX_COLORS.white,
                textAlign: 'center',
                marginTop: 1
              }}
            >
              {Math.round(progress)}%
            </Typography>
          </Box>
        )}

        {/* Show Ready button when feedback is ready and no error */}
        {isFeedbackReady && !hasFeedbackError && (
          <GlassyActionButton variant="orange" onClick={() => navigate(GamerRoutes.LATEST_SESSION_FEEDBACK)}>
            {t('gamer.achievements.unseen.feedbackReady.button', 'Take me there')}
          </GlassyActionButton>
        )}
      </Box>

      {/* Achievements Section - Conditionally render based on unseen/seen (row 3 & 4) */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          flex: 1,
          zIndex: 1,
          position: 'relative',
          overflow: 'visible', // Prevent overflow
          minHeight: '300px'
        }}
      >
        {isShowingUnseen ? (
          <UnseenAchievementsView
            unseenAchievements={unseenAchievements}
            allAchievements={allAchievements}
            onMarkSeen={handleMarkSeen}
            isReportReady={isFeedbackReady}
            onNavigate={() => navigate(GamerRoutes.LATEST_SESSION_FEEDBACK)}
          />
        ) : (
          <SeenAchievementsView
            allAchievements={allAchievements}
            isReportReady={isFeedbackReady}
            onNavigate={() => navigate(GamerRoutes.LATEST_SESSION_FEEDBACK)}
          />
        )}
      </Box>

      {/* Feedback Status Text - Positioned at absolute bottom - Only show error messages */}
      {hasFeedbackError && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
            zIndex: 3
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '24px',
              color: '#FF6B6B',
              opacity: 0.9,
              textAlign: 'center'
            }}
          >
            {t('gamer.achievements.unseen.feedbackError', 'Something went wrong with creating your feedback')}
          </Typography>
        </Box>
      )}
    </PageWrapper>
  )
}
